// src/components/UnitList.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import ExcelReader from '../services/ExcelReader';

const UnitList = ({ onSelectUnit = () => {} }) => {
  const { t, language } = useLanguage();
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showGraphView, setShowGraphView] = useState(false);
  const [pendingFilters, setPendingFilters] = useState({
    unit: 'all',
    startDate: '',
    endDate: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    unit: 'all',
    startDate: '',
    endDate: ''
  });

  const toNumber = (value) => {
    if (typeof value === 'number') {
      return value;
    }
    const parsed = parseFloat(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  useEffect(() => {
    let isMounted = true;

    const loadRows = async () => {
      setIsLoading(true);
      const result = await ExcelReader.readUniteFile('Unite-001.xlsx', 'Unite');

      if (!isMounted) {
        return;
      }

      const mappedRows = result.success
        ? result.data.map((row, index) => ({
            ...row,
            unitName: row.unitName || `${t('units.table.unitFallback')} ${index + 1}`
          }))
        : [];

      setRows(mappedRows);
      setIsLoading(false);
    };

    loadRows();

    return () => {
      isMounted = false;
    };
  }, [t]);

  const availableUnits = useMemo(() => {
    const units = new Set(rows.map((row) => row.unitName).filter(Boolean));
    return Array.from(units).sort((a, b) => a.localeCompare(b, locale));
  }, [rows, locale]);

  const toUtcStartDate = (value) => (value ? new Date(`${value}T00:00:00.000Z`) : null);
  const toUtcEndDate = (value) => (value ? new Date(`${value}T23:59:59.999Z`) : null);

  const filteredRows = useMemo(() => {
    const startDate = toUtcStartDate(appliedFilters.startDate);
    const endDate = toUtcEndDate(appliedFilters.endDate);

    return rows.filter((row) => {
      if (appliedFilters.unit !== 'all' && row.unitName !== appliedFilters.unit) {
        return false;
      }

      if (startDate || endDate) {
        if (!row.timestamp) {
          return false;
        }
        if (startDate && row.timestamp < startDate) {
          return false;
        }
        if (endDate && row.timestamp > endDate) {
          return false;
        }
      }

      return true;
    });
  }, [appliedFilters, rows]);

  const formatDateTime = (date) => {
    if (!date) {
      return t('units.table.noData');
    }
    return date.toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC'
    });
  };

  const summaryStats = useMemo(() => {
    const total = filteredRows.length;
    const attentionCount = filteredRows.filter((row) => row.status === 'attention').length;
    const totalTemperature = filteredRows.reduce((sum, row) => sum + toNumber(row.temperature), 0);
    const totalHumidity = filteredRows.reduce((sum, row) => sum + toNumber(row.humidity), 0);
    const averageTemperature = total ? totalTemperature / total : null;
    const averageHumidity = total ? totalHumidity / total : null;

    return {
      total,
      attentionCount,
      averageTemperature,
      averageHumidity
    };
  }, [filteredRows]);

  const Panel = ({ children, className = '' }) => (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
      {children}
    </div>
  );

  const StatCard = ({ label, value, hint, accentClass = 'text-slate-900' }) => (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-3 text-2xl font-semibold ${accentClass}`}>{value}</p>
      <p className="mt-2 text-xs text-slate-500">{hint}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <Panel className="p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="mt-1 text-2xl font-bold text-slate-900">{t('lists.units.title')}</h2>
            <p className="mt-1 text-sm text-slate-600">{t('lists.units.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
            <button
              type="button"
              onClick={() => setShowGraphView((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              {t('units.graphView.button')}
            </button>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {t('units.totalUnits', { count: filteredRows.length.toLocaleString(locale) })}
            </span>
          </div>
        </div>
      </Panel>

      <Panel className="p-5">
        <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 text-sm font-semibold text-slate-700 sm:flex-row sm:items-center sm:justify-between">
          <span>{t('units.filters.title')}</span>
          <span className="text-xs font-medium text-slate-500">
            {t('units.filters.subtitle')}
          </span>
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-[1.2fr_1fr_1fr_auto]">
          <label className="flex flex-col gap-2 text-sm text-slate-600">
            <span className="font-medium text-slate-700">{t('units.filters.unit')}</span>
            <select
              value={pendingFilters.unit}
              onChange={(event) =>
                setPendingFilters((prev) => ({ ...prev, unit: event.target.value }))
              }
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="all">{t('units.filters.all')}</option>
              {availableUnits.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-2 text-sm text-slate-600">
            <span className="font-medium text-slate-700">{t('units.filters.startDate')}</span>
            <input
              type="date"
              value={pendingFilters.startDate}
              onChange={(event) =>
                setPendingFilters((prev) => ({ ...prev, startDate: event.target.value }))
              }
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <label className="flex flex-col gap-2 text-sm text-slate-600">
            <span className="font-medium text-slate-700">{t('units.filters.endDate')}</span>
            <input
              type="date"
              value={pendingFilters.endDate}
              onChange={(event) =>
                setPendingFilters((prev) => ({ ...prev, endDate: event.target.value }))
              }
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => setAppliedFilters(pendingFilters)}
              className="w-full rounded-xl bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              {t('units.filters.apply')}
            </button>
          </div>
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-4">
        <StatCard
          label={t('units.graphView.cards.totalUnits')}
          value={summaryStats.total.toLocaleString(locale)}
          hint={t('units.graphView.cards.totalUnitsHint')}
        />
        <StatCard
          label={t('units.graphView.cards.attention')}
          value={summaryStats.attentionCount.toLocaleString(locale)}
          hint={t('units.graphView.cards.attentionHint')}
          accentClass="text-amber-600"
        />
        <StatCard
          label={t('units.graphView.cards.avgTemperature')}
          value={
            summaryStats.averageTemperature !== null
              ? `${summaryStats.averageTemperature.toFixed(1)}${t('units.celsius')}`
              : '-'
          }
          hint={t('units.graphView.cards.avgTemperatureHint')}
        />
        <StatCard
          label={t('units.graphView.cards.avgHumidity')}
          value={
            summaryStats.averageHumidity !== null
              ? `${summaryStats.averageHumidity.toFixed(1)}${t('units.percent')}`
              : '-'
          }
          hint={t('units.graphView.cards.avgHumidityHint')}
        />
      </div>

      {showGraphView && (
        <section className="space-y-4">
          <Panel className="p-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{t('units.graphView.title')}</h3>
                <p className="text-sm text-slate-600">{t('units.graphView.subtitle')}</p>
              </div>
              <div className="text-xs text-slate-500">
                {t('units.graphView.lastUpdate', { value: new Date().toLocaleString(locale) })}
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {filteredRows.slice(0, 6).map((row) => (
                <div
                  key={row.id}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900">{row.unitName}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        row.status === 'attention'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {t(`units.status.${row.status}`)}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">{row.location}</div>
                  <div className="mt-3 text-sm font-medium text-slate-700">
                    {row.temperature}
                    {t('units.celsius')} / {row.humidity}
                    {t('units.percent')}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </section>
      )}

      {isLoading ? (
        <Panel className="p-8 text-center">
          <p className="text-sm font-medium text-slate-700">Dosya listesi yükleniyor...</p>
        </Panel>
      ) : filteredRows.length === 0 ? (
        <Panel className="p-8 text-center">
          <p className="text-lg font-semibold text-slate-900">{t('lists.empty.title')}</p>
          <p className="mt-2 text-sm text-slate-600">{t('lists.empty.description')}</p>
        </Panel>
      ) : (
        <Panel className="overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">{t('lists.table.index')}</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">{t('units.table.unit')}</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">{t('units.table.location')}</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">{t('units.table.dateTime')}</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">{t('units.table.temperature')}</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">{t('units.table.humidity')}</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">{t('units.table.status')}</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">{t('units.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => onSelectUnit(row)}
                >
                  <td className="px-4 py-3 text-slate-600">{row.index}</td>
                  <td className="px-4 py-3 text-slate-900 font-medium">{row.unitName}</td>
                  <td className="px-4 py-3 text-slate-600">{row.location}</td>
                  <td className="px-4 py-3 text-slate-700">{formatDateTime(row.timestamp)}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {row.temperature}
                    {t('units.celsius')}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {row.humidity}
                    {t('units.percent')}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                        row.status === 'attention'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {t(`units.status.${row.status}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelectUnit(row);
                        }}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        {t('units.actions.detail')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
};

export default UnitList;
